import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import RelationshipWorkspace from "./RelationshipWorkspace";

const fmtConf = (c) => (c == null ? "—" : `${(c * 100).toFixed(1)}%`);
const fmtVal = (v) => Array.isArray(v) ? v.join(", ") : (typeof v === "object" && v !== null ? JSON.stringify(v) : String(v ?? ""));

export default function KnowledgeMetadataReview({ collections = [], collectionId = null, onClose, onChanged }) {
  const [proposals, setProposals] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [cols, setCols] = useState(collections);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      api.get("/knowledge/metadata-proposals", { params: { status: "pending", ...(collectionId ? { collection_id: collectionId } : {}) } }),
      api.get("/knowledge/collections"),
    ]);
    setProposals(p.data.proposals || []);
    setCols(c.data.collections || []);
    setSelected(new Set());
  }, [collectionId]);
  useEffect(() => { load(); }, [load]);

  const filterCol = collectionId ? cols.find((c) => c.id === collectionId) : null;
  const displayCols = collectionId ? cols.filter((c) => c.id === collectionId) : cols;
  const enabledCols = cols.filter((c) => c.ai_metadata_enrichment_enabled);

  const toggleCol = async (c) => {
    await api.patch(`/knowledge/collections/${c.id}/enrichment`, { enabled: !c.ai_metadata_enrichment_enabled });
    toast.success(`Enrichment ${c.ai_metadata_enrichment_enabled ? "disabled" : "enabled"} for ${c.name}`);
    load();
  };
  const enrichCol = async (c) => {
    setBusy(true);
    try {
      const r = await api.post(`/knowledge/collections/${c.id}/enrich`, null, { params: { limit: 25 } });
      toast.success(`Enrichment queued for ${r.data.queued} document(s). Refresh in a moment.`);
      setTimeout(load, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrichment failed"); }
    finally { setBusy(false); }
  };

  const decide = async (p, action) => {
    setBusy(true);
    try {
      if (action === "accept") {
        const body = edits[p.id] !== undefined ? { final_value: edits[p.id] } : {};
        await api.post(`/knowledge/metadata-proposals/${p.id}/accept`, body);
      } else {
        await api.post(`/knowledge/metadata-proposals/${p.id}/reject`);
      }
      onChanged?.(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      await api.post("/knowledge/metadata-proposals/bulk-approve", { proposal_ids: [...selected] });
      toast.success(`Approved ${selected.size} proposal(s)`);
      onChanged?.(); load();
    } catch { toast.error("Bulk approve failed"); }
    finally { setBusy(false); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-6" data-testid="kc-metadata-review">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Icons.Sparkles className="h-5 w-5 text-indigo-500" />
            <h2 className="font-heading text-lg font-bold text-[#171B2E]">AI Metadata Enrichment</h2>
            {filterCol && <span data-testid="kc-metadata-review-filter" className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">Filtered · {filterCol.name}</span>}
          </div>
          <button onClick={onClose} data-testid="kc-metadata-review-close" className="p-1.5 rounded-lg hover:bg-slate-100"><Icons.X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Enrichment-enabled collections */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{filterCol ? "Selected collection" : "Enrichment-enabled collections"}</h3>
            <div className="space-y-2" data-testid="kc-enrichment-collections">
              {displayCols.map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
                  <button onClick={() => toggleCol(c)} data-testid={`kc-enrich-toggle-${c.id}`}
                    className={`relative h-5 w-9 rounded-full transition-colors ${c.ai_metadata_enrichment_enabled ? "bg-indigo-600" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${c.ai_metadata_enrichment_enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                  <span className="text-sm text-slate-700 flex-1">{c.name}</span>
                  {c.ai_metadata_enrichment_enabled && (
                    <button onClick={() => enrichCol(c)} disabled={busy} data-testid={`kc-enrich-run-${c.id}`}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 disabled:opacity-50">
                      <Icons.Wand2 className="h-3.5 w-3.5" />Enrich
                    </button>
                  )}
                </div>
              ))}
            </div>
            {enabledCols.length === 0 && <p className="text-xs text-slate-400 mt-1">No collections enabled. Toggle one on to begin.</p>}
          </div>

          {/* Review queue */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Review queue {proposals ? `· ${proposals.length} pending` : ""}</h3>
              <button onClick={bulkApprove} disabled={!selected.size || busy} data-testid="kc-bulk-approve"
                className="text-xs font-semibold rounded-lg px-3 py-1.5 bg-emerald-600 text-white disabled:opacity-40 inline-flex items-center gap-1">
                <Icons.CheckCheck className="h-3.5 w-3.5" />Bulk approve ({selected.size})
              </button>
            </div>
            {proposals === null ? <div className="py-8 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-indigo-500 inline" /></div> :
              proposals.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center" data-testid="kc-proposals-empty">No pending metadata proposals. Run enrichment on an enabled collection to generate some.</p> :
              <div className="space-y-2" data-testid="kc-proposals-list">
                {proposals.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-200 p-3" data-testid={`kc-proposal-${p.id}`}>
                    <div className="flex items-start gap-2.5">
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)}
                        data-testid={`kc-proposal-select-${p.id}`} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-slate-700">{p.field}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">conf {fmtConf(p.confidence)}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{p.reason}</span>
                          {p.item_title && <span className="text-[10px] text-slate-400 truncate">· {p.item_title}</span>}
                        </div>
                        <input value={edits[p.id] !== undefined ? fmtVal(edits[p.id]) : fmtVal(p.proposed_value)}
                          onChange={(e) => setEdits((m) => ({ ...m, [p.id]: e.target.value }))}
                          data-testid={`kc-proposal-value-${p.id}`}
                          className="mt-1.5 w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" />
                        {p.source_excerpt && <p className="text-[11px] text-slate-400 mt-1 italic">“{p.source_excerpt}”</p>}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <button onClick={() => decide(p, "accept")} disabled={busy} data-testid={`kc-proposal-accept-${p.id}`}
                          className="text-xs font-semibold rounded-md px-2.5 py-1 bg-emerald-600 text-white disabled:opacity-40">Accept</button>
                        <button onClick={() => decide(p, "reject")} disabled={busy} data-testid={`kc-proposal-reject-${p.id}`}
                          className="text-xs font-semibold rounded-md px-2.5 py-1 border border-slate-200 text-slate-600 disabled:opacity-40">Reject</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
          </div>

          {/* Relationship Resolution Workspace (Phase C) — approved edges become searchable */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Document relationships</h3>
            <RelationshipWorkspace collectionId={collectionId} onChanged={onChanged} />
          </div>
        </div>
      </div>
    </div>
  );
}
